/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 10 2023
 **/

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { Button, Column, Divider, Icon, Menu, Row, Typography, useModal } from "@/frameworks/designStuff/components";
import { UserItem } from "@/frameworks/core/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _P, _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Properties } from "@/frameworks/libraries/properties";
import { APIErrorCode, POPOVER_ID, ModalID } from "@/frameworks/core/commons";
import { useChatRoomActions, useDMRoomState, useSpaceChannelState } from "@/frameworks/core/chat";
import { useChat } from "@/frameworks/core/API";
import { useConfirm, usePopover, usePeopleManagerHandler, usePhoneCall } from "@/frameworks/core/hooks";
import { usePersistUser } from "@/frameworks/core/persistence";
import { JotaiStore } from "@/frameworks/libraries";

import * as S from "./emotion";
import { InviteChatMember } from "../InviteChatMember";
import { VoiceCallRoomAtom } from "../../Provider/voicecall/VoiceCallProvider";

interface ShowChatMember {
	onHide: () => void;
	chatKind: ChatServices.RoomType;
	channelID: string;
	spaceID?: string;
	me?: string;
}

// const positionMap = {
// 	USER: _T("채널 멤버"),
// 	GUEST: _T("게스트"),
// 	SPACE_USER: _T("채널 멤버"),
// 	GROUP_USER: _T("채널 멤버"),
// 	GROUP_ADMIN: _T("관리자"),
// 	SPACE_ADMIN: _T("관리자")
// } as Record<NonNullable<WebServices.Chat.GetRoom.Data["users"][number]["spaceAuthority"]>, string>;

export default function ShowChatMember({ spaceID, channelID, chatKind, onHide, me }: ShowChatMember) {
	const { confirm } = useConfirm();
	const phoneCall = usePhoneCall();
	const myInfo = usePersistUser("me");
	const voiceCallRooms = useAtomValue(VoiceCallRoomAtom, { store: JotaiStore });
	const modal = useModal();
	const popover = usePopover();
	const [spaceChatUserList, setSpaceChatUserList] = useState<WebServices.Chat.GetRoom.Data["users"]>([]);
	const chatAPI = useChat();
	const [isMouseOver, setIsMouseOver] = useState<number>(-1);
	const chatRoomActions = useChatRoomActions();
	const dmRoom = useDMRoomState(channelID);
	const spaceChannel = useSpaceChannelState(spaceID ?? "");
	const { updatePeople } = usePeopleManagerHandler();
	const [userList, setUserList] = useState<WebServices.Chat.GetRoom.Data["users"]>([]);
	const channelChatInfo = chatKind === "Space" ? spaceChannel?.rooms.find((room) => room.roomId === channelID) : null;
	useEffect(() => {
		if (channelChatInfo && channelChatInfo.deleted && channelChatInfo.roomJoined) {
			modal.hide(ModalID.SHOW_CHAT_MEMBER);
		}
	}, [channelChatInfo]);

	useEffect(() => {
		if (chatKind === "Space") {
			chatAPI.getRoom.execute({ roomID: channelID }).then(({ data }) => {
				if (data) {
					setSpaceChatUserList(data.users);
				}
			});
		}
	}, []);

	useEffect(() => {
		if (chatKind === "DM") {
			setUserList(dmRoom?.users ?? []);
		} else if (chatKind === "Space") {
			setUserList(spaceChatUserList);
		}
	}, [spaceChatUserList]);

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();

		const { modal: _modal, type } = e.currentTarget.dataset;
		if (type) {
			switch (type) {
				case "cancel":
					break;
				case "confirm":
					break;
			}

			onHide();
		}

		if (_modal) {
			switch (_modal) {
				case "invite-chat-member":
					{
						modal.change(ModalID.SHOW_CHAT_MEMBER, {
							id: ModalID.INVITE_CHAT_MEMBER,
							content: (
								<InviteChatMember
									channelID={channelID}
									spaceID={spaceID}
									chatKind={chatKind}
									onHide={() => {
										modal.hideAll();
									}}
								/>
							)
						});
					}
					break;
			}
		}
	};

	const openDM = async (id: string) => {
		try {
			await chatRoomActions.dm
				.create({
					userIDs: [id]
				})
				.then((data) => {
					if (!data) return;
					chatRoomActions.common.open({ channelID: data.id, type: "DM" });
				});
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					// const DMRoom = chatRoomListActions.getDMRoom(roomId);
					// if (DMRoom?.hide) {
					// 	chatRoomActions.dm.hide({ channelID: roomId, hide: false });
					// }
					chatRoomActions.common.open({ channelID: roomId, type: "DM" });
				}
			}
			onHide();
		}
	};

	const handleBlockMemberChat = async (userId: string) => {
		try {
			popover.hide(POPOVER_ID.ADD_CALL_USER);
			const userExistsCall = phoneCall.state.active.activeCallState
				? Object.values(phoneCall.state.active.activeCallState).some(
						(user) => user.userId === userId && user.status === "ACCEPTED"
					)
				: false;

			const userExistsVoiceCall = voiceCallRooms
				? Object.values(voiceCallRooms).some((room) => {
						const hasConfigureUser = room.members?.some((member) => member.id === Configure.userID);
						return hasConfigureUser && room.members?.some((member) => member.id === userId);
					})
				: false;

			if (userExistsCall || userExistsVoiceCall) {
				modal.hide(ModalID.SHOW_CHAT_MEMBER);
				confirm({
					descriptions: userExistsCall
						? _P("상대방과 통화 중인 상태에서는<br/>차단할 수 없습니다.<br/>통화 종료 이후 실행해 주세요.")
						: _P(
								"상대방과 보이스챗 중인 상태에서는<br/>차단할 수 없습니다.<br/>보이스챗 종료 이후 실행해 주세요."
							),
					useOnlyConfirm: true,
					descriptionAlign: "center",
					descriptionWidth: "100%"
				});
			} else {
				await updatePeople({
					type: "block",
					payload: {
						active: true,
						userID: userId
					}
				});
				setUserList((preVal) => preVal.map((user) => (user.id === userId ? { ...user, blocked: true } : user)));
			}
		} catch (error) {
			console.warn("#ShowChatMember# error", error);
		}
	};

	const handleRemoveMemberChat = (userId: string) => {
		popover.hide(POPOVER_ID.ADD_CALL_USER);
		chatAPI.space.leave
			.execute({
				roomId: channelChatInfo?.roomId || "",
				deviceKey: Properties.browserTabID,
				userId: userId
			})
			.then(() => {
				setUserList((preVal) => preVal.filter((user) => user.id !== userId));
			});
	};

	return (
		<S.Wrapper horizonAlign="left">
			<S.Header horizonAlign="distribute" verticalAlign="top">
				<Column gap={3} style={{ width: "calc(100% - 32px)" }}>
					<Typography typoSize="Head6" align="left" ellipsis maxLine={1} style={{ width: "100%" }}>
						<Maybe test={chatKind === "DM"} fallback={channelChatInfo?.roomName}>
							{userList
								?.map((user) => user.name)
								.sort()
								.join(", ")}
						</Maybe>
					</Typography>
					<Typography typoSize="Body4">
						{_T("채팅멤버 {{count}}명", {
							count: userList?.length
						})}
					</Typography>
				</Column>
				<S.HeaderIconList>
					<Button.Icon name="Close24" variant="text" size="large" onClick={onHide} />
				</S.HeaderIconList>
			</S.Header>
			<S.Body horizonAlign="center">
				<Divider type="line" />
				<S.InnerBody>
					<S.InnerContent horizonAlign="right">
						<Maybe
							test={
								(chatKind === "Space" &&
									(channelChatInfo?.general ||
										(channelChatInfo?.private && !channelChatInfo?.general))) ||
								chatKind === "DM"
							}
						>
							<Button size="large" filled="primary" onClick={handleClick} data-modal="invite-chat-member">
								{_T("멤버 추가")}
							</Button>
						</Maybe>
					</S.InnerContent>
					<S.UserList>
						{userList?.map((user, index) => {
							return (
								<S.LI
									key={index}
									onMouseEnter={() => setIsMouseOver(index)}
									onMouseLeave={() => setIsMouseOver(-1)}
								>
									<UserItem
										isAdmin={user.id === channelChatInfo?.ownerId}
										key={index}
										id={user.id}
										data-email={user.username}
										me={me === user.username}
										blocked={user.blocked}
										typeUser="channel"
									>
										<Row gap={4}>
											<S.CheckIconWrap>
												<Maybe test={isMouseOver === index}>
													<Icon name="Chat0124" size="24" onClick={() => openDM(user.id)} />
												</Maybe>
											</S.CheckIconWrap>
											<Maybe test={myInfo.userID !== user.id}>
												<S.CheckIconWrap>
													<Maybe test={isMouseOver === index}>
														<Icon
															name="More24"
															size="24"
															onClick={(ev) =>
																popover.show({
																	popoverID: POPOVER_ID.ADD_CALL_USER,
																	content: (
																		<MoreMenu
																			channelInfo={channelChatInfo}
																			userInfo={user}
																			spaceID={spaceID}
																			onRemoveMemberChat={() =>
																				handleRemoveMemberChat(user.id)
																			}
																			onBlockMemberChat={() =>
																				handleBlockMemberChat(user.id)
																			}
																		/>
																	),
																	anchorEl: ev.currentTarget,
																	direction: { x: "right", y: "top" }
																})
															}
														/>
													</Maybe>
												</S.CheckIconWrap>
											</Maybe>
										</Row>
									</UserItem>
								</S.LI>
							);
						})}
					</S.UserList>
				</S.InnerBody>
			</S.Body>
		</S.Wrapper>
	);
}

function MoreMenu({
	spaceID,
	channelInfo,
	userInfo,
	onRemoveMemberChat,
	onBlockMemberChat
}: {
	spaceID?: string;
	channelInfo?: WebServices.SpaceChat.List.Data[number]["rooms"][number] | null;
	userInfo: WebServices.Chat.GetRoom.Data["users"][number];
	onRemoveMemberChat: () => void;
	onBlockMemberChat: () => void;
}) {
	const chatAPI = useChat();
	const { confirm } = useConfirm();
	const queryClient = useQueryClient();

	const handleChangeOwner = () => {
		confirm(
			{
				title: (
					<Typography
						typoSize="Body3"
						typoColor="neutral_70_100"
						css={{
							width: 375,
							textAlign: "center",
							alignItems: "center",
							display: "block",
							fontWeight: "600",
							fontSize: 15
						}}
						dangerouslySetInnerHTML={{
							__html: _T(
								"{{userName}}님을 {{channelName}}의 관리자로 <br /> 이관하시겠습니까? <br /> (관리자 이관시 멤버 권한으로 변경됩니다.)",
								{
									userName: userInfo.name,
									channelName: channelInfo?.roomName
								}
							)
						}}
					/>
				),
				confirmText: _T("이관하기")
			},
			{
				onConfirm() {
					chatAPI.changeOwner
						.execute({
							roomId: channelInfo?.roomId,
							toMemberId: userInfo.id
						})
						.then(() => {
							queryClient.setQueryData<Map<string, WebServices.SpaceChat.List.Data[number]>>(
								["getSpaceChatList"],
								(oldData) => {
									if (!oldData) return oldData;
									const updatedData = new Map(oldData);
									const spaceData = updatedData.get(spaceID ?? "");
									const channelChatInfo = spaceData?.rooms.find(
										(room) => room.roomId === channelInfo?.roomId
									);
									if (channelChatInfo) {
										channelChatInfo.ownerId = userInfo.id;
									}
									return updatedData;
								}
							);
						})
						.catch((error) => {
							console.error("Failed to change owner:", error);
						});
				}
			}
		);
	};

	return (
		<Menu css={[{ background: "#fff" }]}>
			<Menu.List>
				{Configure.userID !== userInfo.id && !userInfo.blocked && (
					<Menu.Item onClick={onBlockMemberChat}>
						<Typography typoSize="Body3_MD">{_T("차단하기")}</Typography>
					</Menu.Item>
				)}
				{channelInfo?.private && channelInfo?.ownerId === Configure.userID && (
					<>
						<Menu.Item onClick={onRemoveMemberChat}>
							<Typography typoSize="Body3_MD">{_T("채널 멤버 삭제")}</Typography>
						</Menu.Item>
						<Menu.Item onClick={handleChangeOwner}>
							<Typography typoSize="Body3_MD">{_T("채널 관리자 이관")}</Typography>
						</Menu.Item>
					</>
				)}
			</Menu.List>
		</Menu>
	);
}
